/**
 * BookEntry — «Лаборатория превращений. Том I. Миндаль»
 *
 * Cover screen for /book/:slug/entry. Uses the two existing master images
 * (mobile 9:16, desktop 16:9). Images, cropping and darkening layers are
 * NOT modified here.
 *
 * Composition follows an explicit visual reference (do not reinterpret):
 *
 *   Desktop 16:9:
 *     – text block absolutely positioned: left 58%, top 17%,
 *       width 420px, transform translateX(-50%);
 *     – deep noble brown typography;
 *     – two buttons in a row directly under the text.
 *
 *   Mobile 9:16:
 *     – text block absolutely positioned: left 50%, top 18%,
 *       width 78vw, transform translateX(-50%);
 *     – soft milky-white typography;
 *     – one compact button under the text, visible without scroll.
 *
 * Text order (strict, from reference):
 *   1. «ЛАБОРАТОРИЯ ПРЕВРАЩЕНИЙ»
 *   2. decorative separator
 *   3. Two-line title: «Том I» / «Миндаль»
 *   4. decorative separator
 *   5. «Один ингредиент. Десятки превращений.»
 *
 * Buttons use existing routes only — no backend/data changes.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getVolumeEntry } from "@/lib/bookApi";
import { useAuth } from "@/context/AuthContext";

// Walk the published TOC in existing sort order and return the id of the
// first published page. Never re-orders by title.
function firstPublishedPageId(sections) {
  for (const sec of sections || []) {
    if (sec.status && sec.status !== "published") continue;
    for (const p of sec.pages || []) {
      if (!p.status || p.status === "published") return p.id;
    }
    for (const sub of sec.subsections || []) {
      if (sub.status && sub.status !== "published") continue;
      for (const p of sub.pages || []) {
        if (!p.status || p.status === "published") return p.id;
      }
    }
  }
  return null;
}

// Small decorative separator: hairline · diamond · hairline.
// Kept as a component so both breakpoints stay identical in structure and
// only colour differs.
function Separator({ color, className = "" }) {
  return (
    <div className={`flex items-center justify-center gap-3 ${className}`}
         aria-hidden data-testid="book-entry-separator">
      <span className="block h-px w-10" style={{ background: color, opacity: 0.9 }} />
      <span className="text-[10px] leading-none" style={{ color, opacity: 0.9 }}>✦</span>
      <span className="block h-px w-10" style={{ background: color, opacity: 0.9 }} />
    </div>
  );
}

export default function BookEntry() {
  const { slug = "almond" } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let stop = false;
    (async () => {
      try {
        const d = await getVolumeEntry(slug);
        if (!stop) setData(d);
      } catch (e) {
        if (!stop) setErr(e?.response?.data?.detail || "Не удалось загрузить Том.");
      } finally {
        if (!stop) setLoading(false);
      }
    })();
    return () => { stop = true; };
  }, [slug]);

  const firstPageId = useMemo(
    () => (data?.intro_pages || []).find(p => p.status === "published")?.id
      || firstPublishedPageId(data?.sections),
    [data]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#efe8dc]"
           data-testid="book-entry-loading" />
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#efe8dc] text-[#3b2b1e] px-6"
           data-testid="book-entry-error">
        <div className="text-center max-w-md">
          <div className="font-serif text-2xl mb-3">Том не найден</div>
          <div className="text-sm opacity-80">{err || "Проверьте адрес ссылки."}</div>
          <button
            className="mt-6 px-6 py-3 rounded-xl bg-[#3b2b1e] text-[#efe8dc]"
            onClick={() => nav("/hall")}
            data-testid="book-entry-back-hall">
            Вернуться в холл
          </button>
        </div>
      </div>
    );
  }

  const { volume } = data;

  const openFirstPage = () => {
    if (firstPageId) nav(`/book/${volume.slug}/pages/${firstPageId}`);
    else nav(`/book/${volume.slug}`);
  };
  const openContents = () => nav(`/book/${volume.slug}`);

  // ------- Colours (per reference) -------
  const DESKTOP_TEXT  = "#1e0f09";  // deep noble brown
  const DESKTOP_TITLE = "#160a06";
  const MOBILE_TEXT   = "#fffaf0";  // soft milky white

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#efe8dc]"
         data-testid="book-entry-screen">

      {/* ============ Mobile master (vertical 9:16) — untouched ============ */}
      <div
        className="lg:hidden absolute inset-0 z-0 bg-cover bg-center"
        style={{
          backgroundImage: `url("${volume.entry_mobile_image_url || "/almond-book/entry-mobile.jpeg"}")`,
        }}
        data-testid="book-entry-bg-mobile"
      />
      {/* subtle gradient for text legibility on mobile — preserved */}
      <div className="lg:hidden absolute inset-0 z-[1]"
           style={{
             background:
               "linear-gradient(180deg, rgba(20,10,4,0.15) 0%, rgba(20,10,4,0.05) 30%, rgba(20,10,4,0.45) 78%, rgba(20,10,4,0.65) 100%)",
           }}
      />

      {/* ============ Desktop master (16:9) — untouched ============ */}
      <div
        className="hidden lg:block absolute inset-0 z-0 bg-cover bg-center"
        style={{
          backgroundImage: `url("${volume.entry_desktop_image_url || "/almond-book/entry-desktop.png"}")`,
        }}
        data-testid="book-entry-bg-desktop"
      />

      {/* ============ Mobile content — top 18%, centred, 78vw ============ */}
      <div
        className="lg:hidden absolute z-10 text-center"
        style={{
          left: "50%",
          top: "18%",
          width: "78vw",
          transform: "translateX(-50%)",
          color: MOBILE_TEXT,
        }}
        data-testid="book-entry-block-mobile"
      >
        <div className="text-[10px] tracking-[0.42em] uppercase"
             data-testid="book-entry-eyebrow-mobile">
          Лаборатория превращений
        </div>
        <Separator color={MOBILE_TEXT} className="my-4" />
        <h1
          className="font-serif leading-[0.98]"
          style={{
            fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
            fontSize: "clamp(40px, 11vw, 60px)",
            color: MOBILE_TEXT,
          }}
          data-testid="book-entry-title-mobile"
        >
          <span className="block">Том I</span>
          <span className="block">Миндаль</span>
        </h1>
        <Separator color={MOBILE_TEXT} className="my-4" />
        <div className="italic font-serif leading-[1.35]"
             style={{ fontSize: "clamp(14px, 3.6vw, 17px)" }}
             data-testid="book-entry-tagline-mobile">
          Один ингредиент. Десятки превращений.
        </div>
        <div className="mt-8 flex justify-center">
          <button
            onClick={openFirstPage}
            data-testid="book-entry-open-first-mobile"
            className="px-6 py-3 rounded-xl tracking-[0.18em] text-[12px] uppercase border transition-colors"
            style={{
              borderColor: "rgba(244,236,220,0.85)",
              color: MOBILE_TEXT,
              background: "rgba(20,10,4,0.28)",
              backdropFilter: "blur(2px)",
            }}>
            Открыть первую страницу →
          </button>
        </div>
      </div>

      {/* ============ Desktop content — left 58%, top 17%, width 420 ============ */}
      <div
        className="hidden lg:block absolute z-10 text-center"
        style={{
          left: "45%",
          top: "17%",
          width: "420px",
          transform: "translateX(-50%)",
          color: DESKTOP_TEXT,
        }}
        data-testid="book-entry-block-desktop"
      >
        <div className="text-[11px] tracking-[0.5em] uppercase"
             data-testid="book-entry-eyebrow-desktop">
          Лаборатория превращений
        </div>
        <Separator color={DESKTOP_TEXT} className="my-6" />
        <h1
          className="font-serif leading-[0.98]"
          style={{
            fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
            fontSize: "clamp(52px, 5vw, 88px)",
            color: DESKTOP_TITLE,
          }}
          data-testid="book-entry-title-desktop"
        >
          <span className="block">Том I</span>
          <span className="block">Миндаль</span>
        </h1>
        <Separator color={DESKTOP_TEXT} className="my-6" />
        <div className="italic font-serif leading-[1.4]"
             style={{ fontSize: "clamp(16px, 1.3vw, 19px)", color: DESKTOP_TEXT }}
             data-testid="book-entry-tagline-desktop">
          Один ингредиент. Десятки превращений.
        </div>
        <div className="mt-8 flex items-center justify-center gap-3">
          <button
            onClick={openFirstPage}
            data-testid="book-entry-open-first-desktop"
            className="px-6 py-3 rounded-xl tracking-[0.14em] text-[12.5px] transition-opacity hover:opacity-90"
            style={{ background: DESKTOP_TITLE, color: "#efe4d0" }}>
            Открыть первую страницу →
          </button>
          <button
            onClick={openContents}
            data-testid="book-entry-contents-desktop"
            className="px-6 py-3 rounded-xl tracking-[0.14em] text-[12.5px] border transition-colors hover:bg-[#3b241a]/5"
            style={{ borderColor: DESKTOP_TEXT, color: DESKTOP_TEXT }}>
            Перейти к содержанию
          </button>
        </div>
      </div>

      {/* Top-left return arrow — preserved */}
      <button
        onClick={() => nav(user ? "/hall" : "/")}
        data-testid="book-entry-back"
        className="absolute z-20 top-5 left-5 lg:top-8 lg:left-8 w-11 h-11 rounded-full flex items-center justify-center backdrop-blur-md
                   bg-white/15 text-[#f9f2e4] lg:bg-[#2a1810]/10 lg:text-[#2a1810]"
        aria-label="Назад"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
    </div>
  );
}
